import React, { useMemo } from "react";
import { schema } from "../schema";
import { fmtDur, getMillis, SITUATIONS_MAP, getResultLabel } from "./helpers";

/**
 * CaseReport — entregable de casos validados (Fase 2.5).
 *
 * Dos modos sobre la misma data:
 *   mode="case"   → ficha de un caso (1 caso = 1 página)
 *   mode="report" → reporte del periodo: portada resumen + una ficha por caso
 *
 * Se muestra en pantalla como "hoja de papel" y se imprime/guarda como PDF con
 * el diálogo del navegador (@media print). Sin dependencias nuevas.
 *
 * Props:
 *   cases: array de alarmas validadas (o 1 en modo case)
 *   facility: doc del recinto (title, company)
 *   user: quien genera el documento
 *   periodLabel: texto del periodo (ej. "Hoy" / "Esta semana" / "Este mes")
 *   totalRelevant: nº de eventos relevantes del periodo (para el resumen)
 *   onClose: () => void
 */

const PAPER = {
  bg: "#f7f6f2",
  ink: "#1a2233",
  muted: "#5a6478",
  line: "#d8d5cc",
};

function fmtStamp(ms) {
  if (!ms) return "—";
  return new Date(ms).toLocaleString("es-CL", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
}

function PField({ k, v, full }) {
  return (
    <div style={{ gridColumn: full ? "1 / -1" : undefined, padding: "5px 0", borderBottom: `1px solid ${PAPER.line}` }}>
      <div style={{ fontSize: 8.5, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".06em", color: PAPER.muted }}>{k}</div>
      <div style={{ fontSize: 11.5, marginTop: 1 }}>{v || "—"}</div>
    </div>
  );
}

/** Una ficha completa de un caso. */
function CaseSheet({ a, facility, generatedBy, generatedAt, pageBreak }) {
  const createdMs = a.createdAt?.seconds ? a.createdAt.seconds * 1000 : null;
  const closedMs = a.resolvedAt?.seconds ? a.resolvedAt.seconds * 1000 : null;
  const dispatchMs = a.dispatchTime ? getMillis(a.dispatchTime) : null;
  const arrivedMs = a.arrivedTime ? getMillis(a.arrivedTime) : null;
  const dispatchDelta = dispatchMs && createdMs ? Math.max(0, Math.floor((dispatchMs - createdMs) / 1000)) : null;
  const arrivalDelta = arrivedMs && dispatchMs ? Math.max(0, Math.floor((arrivedMs - dispatchMs) / 1000)) : null;
  const bars = a.signalBarsNum ?? (a.signalBars && a.signalBars !== "not_specified" ? Number(a.signalBars) : null);
  const guards = (Array.isArray(a.guards) && a.guards.length ? a.guards.join(", ") : a.guardName) || null;
  const plate = a.vehicle?.plate || a.plate || null;

  const reviewedMs = a.reviewedAt?.seconds ? a.reviewedAt.seconds * 1000 : null;
  const validatedMs = a.validatedAt?.seconds ? a.validatedAt.seconds * 1000 : null;

  return (
    <div className="case-sheet" style={{ pageBreakBefore: pageBreak ? "always" : undefined, breakBefore: pageBreak ? "page" : undefined }}>
      {/* Membrete */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", borderBottom: `2.5px solid ${PAPER.ink}`, paddingBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
          <div style={{ width: 30, height: 30, borderRadius: 6, background: "#dc2626", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 15 }}>T</div>
          <div>
            <div style={{ fontWeight: 800, fontSize: 14, letterSpacing: "-.01em" }}>{schema.platformName} · Tank SpA</div>
            <div style={{ fontSize: 9.5, color: PAPER.muted }}>Sistema de detección y gestión de inhibidores</div>
          </div>
        </div>
        <div style={{ textAlign: "right", fontFamily: "var(--mono)", fontSize: 10, color: PAPER.muted }}>
          FICHA DE CASO VALIDADO
          <b style={{ display: "block", fontSize: 12.5, color: PAPER.ink }}>{a.caseFolio || "s/folio"}</b>
          ID {a.docID}
        </div>
      </div>

      <h3 style={{ margin: "16px 0 3px", fontSize: 16 }}>
        {a.zoneName || "—"}
        <span style={{ fontWeight: 400, fontSize: 12, color: PAPER.muted }}> · {facility?.title || ""}</span>
      </h3>
      <span style={{ display: "inline-block", fontSize: 9.5, fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase", background: "#14532d", color: "#ecfdf5", borderRadius: 4, padding: "2px 8px" }}>
        ✓ Caso validado · {getResultLabel(a)}
      </span>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2px 22px", marginTop: 14 }}>
        <PField k="Fecha y hora del evento" v={fmtStamp(createdMs)} />
        <PField k="Duración de la señal" v={a.duration != null ? fmtDur(a.duration) : "—"} />
        <PField k="Situación encontrada" v={a.situation ? `${a.situation} · ${SITUATIONS_MAP[a.situation] || ""}` : "—"} />
        <PField k="Barras de señal" v={bars != null ? `${bars} de 6` : "—"} />
        <PField k="Operador CCTV" v={`${a.dispatchedBy || a.closedBy || "—"}${dispatchDelta != null ? ` · despacho en ${fmtDur(dispatchDelta)}` : ""}`} />
        <PField k="Guardias en zona" v={`${guards || "—"}${arrivalDelta != null ? ` · llegada ${fmtDur(arrivalDelta)}` : ""}`} />
        {plate && <PField k="Vehículo" v={`${plate}${a.vehicle?.desc ? ` — ${a.vehicle.desc}` : ""}`} />}
        {(a.suspectDesc || a.suspect) && <PField k="Persona sospechosa" v={a.suspectDesc || "Sí"} />}
        {(a.notes || a.comments) && <PField k="Relato del cierre" v={a.notes || a.comments} full />}
        {a.lateReason && <PField k="Motivo de gestión tardía" v={a.lateReason} full />}
      </div>

      {/* Cadena de evidencia */}
      <div style={{ marginTop: 16, border: `1.5px solid ${PAPER.ink}`, borderRadius: 6, padding: "11px 13px" }}>
        <div style={{ fontSize: 9.5, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 7 }}>Cadena de evidencia</div>
        <div style={{ display: "flex", gap: 9, alignItems: "baseline", fontSize: 11, padding: "2.5px 0" }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#64748b", flexShrink: 0 }} />
          <span>Evento cerrado por <b>{a.closedBy || a.guardName || "—"}</b> (operador CCTV)</span>
          <span style={{ marginLeft: "auto", fontFamily: "var(--mono)", fontSize: 10, color: PAPER.muted, whiteSpace: "nowrap" }}>{fmtStamp(closedMs)}</span>
        </div>
        {a.reviewedBy && (
          <div style={{ display: "flex", gap: 9, alignItems: "baseline", fontSize: 11, padding: "2.5px 0" }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#f59e0b", flexShrink: 0 }} />
            <span>Revisado por <b>{a.reviewedBy}</b></span>
            <span style={{ marginLeft: "auto", fontFamily: "var(--mono)", fontSize: 10, color: PAPER.muted, whiteSpace: "nowrap" }}>{fmtStamp(reviewedMs)}</span>
          </div>
        )}
        <div style={{ display: "flex", gap: 9, alignItems: "baseline", fontSize: 11, padding: "2.5px 0" }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#22c55e", flexShrink: 0 }} />
          <span><b>Validado por {a.validatedBy || "—"}</b></span>
          <span style={{ marginLeft: "auto", fontFamily: "var(--mono)", fontSize: 10, color: PAPER.muted, whiteSpace: "nowrap" }}>{fmtStamp(validatedMs)}</span>
        </div>
      </div>

      <div style={{ marginTop: 18, paddingTop: 10, borderTop: `1px solid ${PAPER.line}`, fontSize: 8.5, color: PAPER.muted, lineHeight: 1.5 }}>
        Documento generado por Tank Mission Control el {fmtStamp(generatedAt)}
        {generatedBy ? ` a solicitud de ${generatedBy}` : ""}. La información es fiel al registro del sistema
        al momento de la emisión y es verificable con el ID del evento.
      </div>
    </div>
  );
}

export default function CaseReport({ cases, facility, user, periodLabel, totalRelevant, onClose }) {
  const list = useMemo(() => (Array.isArray(cases) ? cases.filter(Boolean) : []), [cases]);
  const isReport = list.length > 1;
  const generatedAt = Date.now();
  const generatedBy = user?.email || null;

  const range = useMemo(() => {
    const times = list.map((a) => (a.createdAt?.seconds ? a.createdAt.seconds * 1000 : null)).filter(Boolean).sort((x, y) => x - y);
    if (times.length === 0) return null;
    const f = (ms) => new Date(ms).toLocaleDateString("es-CL", { day: "2-digit", month: "short", year: "numeric" });
    return times.length === 1 ? f(times[0]) : `${f(times[0])} al ${f(times[times.length - 1])}`;
  }, [list]);

  return (
    <div
      onClick={onClose}
      className="case-report-overlay"
      style={{
        position: "fixed", inset: 0, zIndex: 10060,
        background: "rgba(2,6,17,0.86)", backdropFilter: "blur(6px)",
        overflowY: "auto", padding: "18px 14px",
      }}
    >
      {/* Barra de acciones (no se imprime) */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="no-print"
        style={{
          maxWidth: 640, margin: "0 auto 14px", display: "flex", alignItems: "center", gap: 8,
          background: "var(--card)", border: "1px solid var(--border)", borderRadius: 10, padding: "10px 12px",
        }}
      >
        <div>
          <div style={{ fontSize: 12.5, fontWeight: 800, color: "var(--bright)" }}>
            {isReport ? `📄 Reporte de casos validados · ${list.length}` : "📄 Ficha de caso validado"}
          </div>
          <div style={{ fontSize: 9.5, color: "var(--muted)", marginTop: 1 }}>
            Usa “Imprimir” y elige <b>Guardar como PDF</b> para descargarlo o enviarlo.
          </div>
        </div>
        <span style={{ flex: 1 }} />
        <button
          type="button"
          onClick={() => window.print()}
          style={{ padding: "7px 12px", borderRadius: 7, border: "1px solid #22c55e", background: "#22c55e", color: "#08130b", fontSize: 11, fontWeight: 700, cursor: "pointer" }}
        >🖨️ Imprimir / PDF</button>
        <button
          type="button"
          onClick={onClose}
          style={{ padding: "7px 12px", borderRadius: 7, border: "1px solid var(--border)", background: "var(--surface)", color: "var(--subtle)", fontSize: 11, fontWeight: 700, cursor: "pointer" }}
        >✕ Cerrar</button>
      </div>

      {/* Hoja(s) */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="case-paper"
        style={{
          width: "100%", maxWidth: 640, margin: "0 auto",
          background: PAPER.bg, color: PAPER.ink,
          borderRadius: 4, padding: "30px 34px", fontSize: 12, lineHeight: 1.5,
          boxShadow: "0 24px 60px -12px rgba(0,0,0,.85)",
        }}
      >
        {list.length === 0 ? (
          <div style={{ textAlign: "center", color: PAPER.muted, padding: "30px 0" }}>
            No hay casos validados en esta selección.
          </div>
        ) : (
          <>
            {/* Portada del reporte */}
            {isReport && (
              <div style={{ marginBottom: 26 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", borderBottom: `2.5px solid ${PAPER.ink}`, paddingBottom: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                    <div style={{ width: 26, height: 26, borderRadius: 6, background: "#dc2626", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 13 }}>T</div>
                    <div>
                      <div style={{ fontWeight: 800, fontSize: 13 }}>Casos validados{periodLabel ? ` · ${periodLabel}` : ""}</div>
                      <div style={{ fontSize: 9.5, color: PAPER.muted }}>
                        {facility?.title || ""}{range ? ` · ${range}` : ""}
                      </div>
                    </div>
                  </div>
                  <div style={{ textAlign: "right", fontFamily: "var(--mono)", fontSize: 9, color: PAPER.muted }}>
                    <b style={{ fontSize: 12, color: PAPER.ink, display: "block" }}>{list.length} casos</b>
                    {totalRelevant ? `de ${totalRelevant} relevantes` : ""}
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "72px 1fr 108px 74px", gap: 8, fontSize: 8, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".05em", color: PAPER.muted, borderBottom: `1.5px solid ${PAPER.ink}`, padding: "8px 6px 4px" }}>
                  <span>Folio</span><span>Zona</span><span>Situación</span><span>Validado</span>
                </div>
                {list.map((a) => (
                  <div key={`idx-${a.docID}`} style={{ display: "grid", gridTemplateColumns: "72px 1fr 108px 74px", gap: 8, fontSize: 9.5, padding: "4px 6px", borderBottom: `1px solid ${PAPER.line}`, alignItems: "center" }}>
                    <span style={{ fontFamily: "var(--mono)" }}>{a.caseFolio || "—"}</span>
                    <span>{a.zoneName || "—"}</span>
                    <span>{SITUATIONS_MAP[a.situation] || getResultLabel(a)}</span>
                    <span style={{ fontFamily: "var(--mono)" }}>
                      {a.validatedAt?.seconds ? new Date(a.validatedAt.seconds * 1000).toLocaleDateString("es-CL", { day: "2-digit", month: "short" }) : "—"}
                    </span>
                  </div>
                ))}
                <div style={{ fontSize: 8.5, color: PAPER.muted, marginTop: 10 }}>
                  A continuación, una ficha detallada por cada caso.
                </div>
              </div>
            )}

            {list.map((a, i) => (
              <CaseSheet
                key={a.docID}
                a={a}
                facility={facility}
                generatedBy={generatedBy}
                generatedAt={generatedAt}
                pageBreak={isReport && i > 0}
              />
            ))}
          </>
        )}
      </div>

      {/* Impresión: solo el documento, en papel blanco */}
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          .case-report-overlay, .case-report-overlay * { visibility: visible !important; }
          .case-report-overlay {
            position: static !important; background: #fff !important; padding: 0 !important;
            backdrop-filter: none !important; overflow: visible !important;
          }
          .case-paper {
            max-width: none !important; box-shadow: none !important; border-radius: 0 !important;
            padding: 0 !important; background: #fff !important;
          }
          .case-sheet + .case-sheet { margin-top: 0; }
          .no-print { display: none !important; }
          @page { margin: 16mm; }
        }
      `}</style>
    </div>
  );
}
